import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Company } from 'src/app/domains/companies/models';
import { CompanyService } from 'src/app/domains/companies/services';
import { NotificationService } from '../../services/notification/notification.service';
import { SmsConfigRequest, SmsConfigResponse, SmsRateLimitsResponse } from 'voxloud-types/dist/models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FraudRateLimitsPopupComponent } from '../fraud-rate-limits-popup/fraud-rate-limits-popup.component';

interface LoadingState {
  rateLimit: boolean;
  smsConfig: boolean;
}

interface FailedState {
  rateLimit: boolean;
  smsConfig: boolean;
}

@Component({
  selector: 'sms-fraud-limits',
  templateUrl: './sms-fraud-limits.component.html',
  styleUrls: ['./sms-fraud-limits.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SmsFraudLimitsComponent implements OnInit, OnDestroy {

  @Input() company: Company | null;
  private smsConfigResponse: SmsConfigResponse;
  private subscriptions: Subscription[] = [];
  private fraudLockEnabledSubject = new BehaviorSubject<boolean>(false);
  private loadingSubject = new BehaviorSubject<LoadingState>({
    rateLimit: false,
    smsConfig: false
  });
  private failedSubject = new BehaviorSubject<FailedState>({
    rateLimit: false,
    smsConfig: false
  });
  private smsRateLimitsSubject = new BehaviorSubject<SmsRateLimitsResponse>({
    company_rate_limit: {
      requests: 0,
      per_seconds: 0
    },
    content_rate_limit: {
      requests: 0,
      per_seconds: 0
    }
  });
  public loading$ = this.loadingSubject.asObservable();
  public failed$ = this.failedSubject.asObservable();
  public smsRateLimits$ = this.smsRateLimitsSubject.asObservable();
  public fraudLockEnabled$ = this.fraudLockEnabledSubject.asObservable();

  constructor(
    private companyService: CompanyService,
    private notification: NotificationService,
    private modalService: NgbModal) { }

  ngOnInit() {
    this.loadSmsConfig();
    this.loadSmsRateLimits();
  }

  public loadSmsRateLimits(): void {
    if (!this.company?.companyId) return;
    this.updateLoadingState('rateLimit', true);
    this.updateFailedState('rateLimit', false);
    this.subscriptions.push(this.companyService.getSmsRateLimits(this.company.companyId).subscribe((rateLimitsDetails: SmsRateLimitsResponse) => {
      this.smsRateLimitsSubject.next(rateLimitsDetails);
      this.updateLoadingState('rateLimit', false);
      this.updateFailedState('rateLimit', false);
    }, () => {
      this.updateLoadingState('rateLimit', false);
      this.updateFailedState('rateLimit', true);
    }));
  }
  public loadSmsConfig(updateFraudRateLimits: boolean = false, fraudRateLimitsDetails: object = null): void {
    if (!this.company?.companyId) return;
    this.updateLoadingState('smsConfig', true);
    this.updateFailedState('smsConfig', false);
    this.subscriptions.push(this.companyService.getSmsConfiguration(this.company.companyId).subscribe((config: SmsConfigResponse) => {
      this.smsConfigResponse = config;
      this.fraudLockEnabledSubject.next(config?.locked || false);
      this.updateLoadingState('smsConfig', false);
      this.updateFailedState('smsConfig', false);
      if (updateFraudRateLimits) {
        this.updateSmsRateDetails(fraudRateLimitsDetails);
      }
    }, () => {
      this.fraudLockEnabledSubject.next(false);
      this.updateLoadingState('smsConfig', false);
      this.updateFailedState('smsConfig', true);
    }));
  }

  public onFraudLockChange(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.setLockStatus(!this.fraudLockEnabledSubject.value);
  }

  public setLockStatus(isLocked: boolean): void {
    if (!this.company?.companyId) return;
    const message = isLocked
      ? 'companies.sms.confirm-sms-lock'
      : 'companies.sms.confirm-sms-unlock';
    this.notification.showConfirmationWithAlphaname(
      message,
      this.company.alphaname
    ).then((result: string) => {
      if (result === 'ok') {
        this.updateLoadingState('smsConfig', true);
        this.companyService.toggleSmsConfiguration(isLocked, this.company.companyId).subscribe(() => {
          const message = isLocked
            ? 'companies.sms.sms-locked'
            : 'companies.sms.sms-unlocked';
          this.notification.success(message);
          this.fraudLockEnabledSubject.next(isLocked);
          this.updateLoadingState('smsConfig', false);
        }, () => {
          this.updateLoadingState('smsConfig', false);
        });
      }
    });
  }

  public editSmsRateLimits(): void {
    const modal = this.modalService.open(FraudRateLimitsPopupComponent, { size: 'md' });
    Object.assign(modal.componentInstance, {
      smsRateLimits: this.smsRateLimitsSubject.value
    });
    this.subscriptions.push(modal.componentInstance.onEmitFraudRateLimits.subscribe((updatedFraudRateLimits) => {
      if (updatedFraudRateLimits) {
        if (this.smsConfigResponse) {
          this.updateSmsRateDetails(updatedFraudRateLimits);
        } else {
          this.loadSmsConfig(true, updatedFraudRateLimits);
        }
      }
    }));
  }


  public updateSmsRateDetails(updatedFraudRateLimits: object): void {
    if (!this.company?.companyId) return;
    this.updateLoadingState('rateLimit', true);
    const currentRateLimits = this.smsRateLimitsSubject.value;
    const smsConfigRequest: SmsConfigRequest = {
      sender_name: this.smsConfigResponse.sender_name,
      alpha_name: this.company.alphaname,
      provider_id: this.smsConfigResponse.provider.id,
      locked: this.fraudLockEnabledSubject.value,
      custom_rate_limits: {
        company_rate_limit: {
          requests: +updatedFraudRateLimits?.['accountRate'],
          per_seconds: currentRateLimits.company_rate_limit.per_seconds
        },
        content_rate_limit: {
          requests: +updatedFraudRateLimits?.['identicalMessageRate'],
          per_seconds: currentRateLimits.content_rate_limit.per_seconds
        }
      }
    }
    this.subscriptions.push(
      this.companyService.updateSmsConfiguration(this.company.companyId, smsConfigRequest).subscribe(() => {
        this.loadSmsRateLimits();
      }, () => {
        this.updateLoadingState('rateLimit', false);
      })
    )
  }

  private updateLoadingState(key: keyof LoadingState, value: boolean): void {
    const currentState = this.loadingSubject.value;
    this.loadingSubject.next({ ...currentState, [key]: value });
  }

  private updateFailedState(key: keyof FailedState, value: boolean): void {
    const currentState = this.failedSubject.value;
    this.failedSubject.next({ ...currentState, [key]: value });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub?.unsubscribe());
    this.loadingSubject.complete();
    this.failedSubject.complete();
    this.smsRateLimitsSubject.complete();
    this.fraudLockEnabledSubject.complete();
  }
}
